import {
  Button,
  Table,
  Tooltip,
  DatePicker,
  message,
  Popconfirm,
  Flex,
} from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FileExcelOutlined,
  EditFilled,
  FileSearchOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { createUseStyles, useTheme } from 'react-jss'
import { Link, useParams } from 'react-router-dom'
import { useAsync, useIsMobile } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import useFilters from '../../hooks/useFilters'
import {
  useOfficeChecks,
  useOfficeChecksPagination,
  useFetchOfficeChecks,
} from '../../redux/slices/office-checks'
import { USER_ROLES } from '../../lib/constants'
import { usePractice } from '../../redux/slices/practices'
import {
  useCanSign,
  useCurrentUser,
  useFeatureGuard,
} from '../../redux/slices/session'
import { toPath } from '../../routes'
import api from '../../api'
import { ListError, PageHeader } from '../Common'
import DiseasesTags from '../Common/DiseasesTags'
import Filters from '../Forms/Filters'
import { formatDOB } from '../../lib/datetime'
import useExportExcel from '../../hooks/useExportExcel'
import useQueryParams from '../../hooks/useQueryParams'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import BatchDownloadButton from './BatchDownloadButton'
import {
  EXPAND_COLUMN_WIDTH,
  OfficeCheckMobileCard,
  OfficeCheckMobileDetails,
} from './OfficeCheckMobileRow'

const { RangePicker } = DatePicker

const useStyles = createUseStyles({
  inactive: {
    color: 'red',
  },
  criticalIcon: {
    color: 'red',
  },
  pageHeader: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: 'auto auto 1fr',
    alignItems: 'center',
  },
  datePicker: {
    marginRight: '1rem',
    marginBottom: '20px',
    // Two date inputs side by side overflow a phone viewport at their natural
    // width, so let the range picker use the full row instead.
    [MOBILE_MEDIA_QUERY]: {
      marginRight: 0,
      width: '100%',
    },
  },
  total: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 20,
  },
  totalResults: {
    background: ({ theme }) => theme.lightGray,
    color: '#707070',
    padding: '0 24px',
    fontWeight: 'bold',
    display: 'flex',
    alignContent: 'center',
    flexWrap: 'wrap',
  },
  officeChecksTable: {
    '& tr td:last-child': {
      textAlign: 'right',
      borderLeft: '1px solid rgb(232, 232, 232)',
      backgroundColor: '#f1f2fc',
    },
    '& tr th:last-child': {
      textAlign: 'center',
      borderLeft: '1px solid rgb(232, 232, 232)',
      backgroundColor: '#f1f2fc',
    },
    '& tr td:last-child button': {
      border: '1px solid #5175b3',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '.5rem',
    justifyContent: 'center',
  },
})

// Defined at module scope so the antd render props aren't flagged as
// inline-defined components (they just return existing component elements).
const renderMobileCard =
  ({ editDates, ...options }) =>
  (_text, report) => {
    const { id } = report
    return (
      <OfficeCheckMobileCard
        report={report}
        editDate={editDates[id]}
        {...options}
      />
    )
  }

const renderMobileDetails = report => (
  <OfficeCheckMobileDetails report={report} />
)

export default function OfficeChecksReports() {
  const { practiceId } = useParams()
  const practice = usePractice(practiceId)

  const user = useCurrentUser()
  const userRole = user.role
  const isPractitioner = userRole === USER_ROLES.PRACTITIONER
  const isAdmin = userRole === USER_ROLES.ADMIN

  const canSign = useCanSign()
  const isMobile = useIsMobile()

  const theme = useTheme()
  const { brandWarning } = theme
  const classes = useStyles({ theme })
  const [editDates, setEditDates] = useState({})

  const DEFAULT_FILTERS = {
    filters: 'all',
    fromDate: dayjs().format('YYYY-MM-DD'),
    toDate: dayjs().format('YYYY-MM-DD'),
  }

  const { handlePageChange, conditions, setConditions } = useFilters({
    ...DEFAULT_FILTERS,
    practiceId,
  })

  const { page, fromDate, toDate, filters } = conditions

  const res = useAsync(
    useFetchOfficeChecks(
      practiceId,
      fromDate,
      toDate,
      page,
      filters,
      conditions,
    ),
  )

  const { pending, error, clearError, execute } = res

  const handleDateChange = useCallback(
    (_, rangeString) => {
      if (rangeString[0] === '' || rangeString[1] === '') return

      setConditions({
        ...conditions,
        fromDate: dayjs(rangeString[0]).format('YYYY-MM-DD'),
        toDate: dayjs(rangeString[1]).format('YYYY-MM-DD'),
        page: 1,
      })
    },
    [setConditions, conditions],
  )

  useEffect(() => {
    execute(
      practiceId,
      dayjs(conditions.fromDate).format('YYYY-MM-DD'),
      dayjs(conditions.toDate).format('YYYY-MM-DD'),
      page,
      filters,
      conditions,
    )
  }, [conditions, execute, practiceId, page, filters])

  const handleReset = useCallback(() => {
    setConditions({
      filters: 'all',
      fromDate: dayjs().format('YYYY-MM-DD'),
      toDate: dayjs().format('YYYY-MM-DD'),
      practiceId,
      page: 1,
    })
  }, [setConditions, practiceId])

  const handleStatusFilter = useCallback(
    ({
      filters: newFilters,
      medicalProviderId,
      deviceTypeId,
      officeLocationId,
      query,
    }) => {
      setConditions({
        ...conditions,
        filters: newFilters,
        medicalProviderId,
        deviceTypeId,
        officeLocationId,
        query,
        page: 1,
      })
    },
    [conditions, setConditions],
  )

  const officeChecks = useOfficeChecks(fromDate, toDate)
  const [, , getPathWithQuery] = useQueryParams({})

  const toOfficeCheck = useCallback(
    id =>
      getPathWithQuery(
        toPath.practices.officeChecks.officeCheck({
          id,
          practiceId,
        }),
        { ...conditions },
      ),
    [practiceId, conditions, getPathWithQuery],
  )

  // Memoised so the mobile column definitions below keep a stable identity.
  const toEditOfficeCheck = useCallback(
    id => {
      const path = toPath.practices.officeChecks.officeCheck.edit({
        id,
        practiceId,
      })

      return `${path}?from_date=${fromDate}&to_date=${toDate}`
    },
    [practiceId, fromDate, toDate],
  )

  const handleDelete = useCallback(
    async id => {
      try {
        await api.officeChecks.destroy(id, practiceId)
        message.success('Office Check successfully deleted')
        execute(practiceId, fromDate, toDate, page, filters, conditions)
      } catch (e) {
        message.error(`There was an error removing this report: ${e.message}`)
      }
    },
    [conditions, execute, filters, fromDate, page, practiceId, toDate],
  )

  const handleSaveDate = useCallback(
    report => {
      const { id } = report
      // The stored value is the original string until the picker changes it,
      // so normalise before serialising.
      const checkDate = dayjs(editDates[id]).toISOString()

      api.officeChecks
        .update(id, practiceId, {
          checkDate,
        })
        .then(() => {
          message.success('Report date updated successfully')
          setEditDates({ ...editDates, [id]: undefined })
          execute(practiceId, fromDate, toDate, page, filters, conditions)
        })
        .catch(e => {
          message.error(
            `There was an error updating the report date: ${e.message}`,
          )
        })
    },
    [
      conditions,
      editDates,
      execute,
      filters,
      fromDate,
      page,
      practiceId,
      toDate,
    ],
  )

  // Mobile equivalent of the inline date editing done in the Report Date column.
  const handleEditDateChange = useCallback((id, value) => {
    setEditDates(current => {
      const next = { ...current }
      if (value) next[id] = value
      else delete next[id]
      return next
    })
  }, [])

  const shouldSign = row =>
    row.hasBiobridgeReport && canSign && !row.signedAt && !row.isArchived

  const columns = [
    {
      title: '',
      dataIndex: 'critical',
      key: 'critical',
      align: 'center',
      width: 40,
      render: critical =>
        critical ? (
          <Tooltip title="Requires Attention">
            <ExclamationCircleOutlined className={classes.criticalIcon} />
          </Tooltip>
        ) : null,
    },
    {
      title: 'Full Name',
      key: 'id',
      render: (_, row) => (
        <>
          <Link
            to={toPath.patients.patient({ id: row.patient.id })}
            style={{ whiteSpace: 'nowrap', marginRight: 5 }}
          >
            <Tooltip title="View Patient">
              {`${row.patient.lastName}, ${row.patient.firstName}`}
            </Tooltip>
          </Link>
          <Link
            to={toPath.patients.patient.edit({ id: row.patient.id })}
            title="Edit Patient"
          >
            <Tooltip title="Edit Patient">
              <EditFilled />
            </Tooltip>
          </Link>
        </>
      ),
    },
    {
      title: 'MRN',
      dataIndex: ['patient', 'practiceMrn'],
      key: 'MRN',
    },
    {
      title: 'DOB',
      dataIndex: ['patient', 'birthDate'],
      key: 'dob',
      render: formatDOB,
    },
    {
      title: 'Relevant History',
      key: 'diseases',
      render: (_, row) => <DiseasesTags patientSummary={row.patient} />,
    },
    {
      title: 'Device Type',
      dataIndex: ['patient', 'currentDevice', 'modelType'],
      key: 'deviceType',
    },
    {
      title: 'CPT Code',
      dataIndex: ['patient', 'currentDevice', 'inOfficeCptCode'],
      key: 'cptCode',
      render: (text, report) =>
        report?.nonBillable ? '' : <span>{text}</span>,
    },
    {
      title: 'ICD-10 Code',
      dataIndex: ['patient', 'currentDevice', 'inOfficeIcdCode'],
      key: 'icdCode',
      render: (text, report) =>
        report?.nonBillable ? '' : <span>{text}</span>,
    },
    {
      title: 'Report Date',
      key: 'checkDate',
      dataIndex: 'checkDate',
      render: (text, report) => {
        if (!editDates?.[report.id]) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{formatDOB(text)}</span>
              <Tooltip title="Edit Patient">
                <Button
                  type="link"
                  icon={<EditFilled />}
                  onClick={() =>
                    setEditDates({ ...editDates, [report.id]: text })
                  }
                />
              </Tooltip>
            </div>
          )
        }
        return (
          <div style={{ width: '200px' }}>
            <DatePicker
              value={dayjs(editDates[report.id])}
              onChange={date =>
                setEditDates({ ...editDates, [report.id]: date })
              }
            />
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleSaveDate(report)}
            />
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={() => {
                const newDates = { ...editDates }
                delete newDates[report.id]
                setEditDates(newDates)
              }}
            />
          </div>
        )
      },
    },
    {
      title: 'Device Report',
      dataIndex: ['hasDeviceReport'],
      key: 'hasDeviceReport',
      align: 'center',
      render(_text, { hasDeviceReport }) {
        return (
          <CircleCheckIcon
            checked={hasDeviceReport}
            falseStyle={{ color: brandWarning }}
          />
        )
      },
    },
    {
      title: 'Completed',
      dataIndex: ['hasBiobridgeReport'],
      key: 'hasBiobridgeReport',
      align: 'center',
      render(_text, { hasBiobridgeReport }) {
        return (
          <CircleCheckIcon
            checked={hasBiobridgeReport}
            falseStyle={{ color: brandWarning }}
          />
        )
      },
    },
    {
      title: 'Signed',
      dataIndex: ['signed'],
      key: 'signedAt',
      align: 'center',
      render(_text, { signedAt }) {
        return (
          <CircleCheckIcon
            checked={signedAt}
            falseStyle={{ color: brandWarning }}
          />
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render(_text, report) {
        return (
          <div className={classes.actions}>
            {!report.hasDeviceReport && (
              <>
                <Link to={toEditOfficeCheck(report.id)}>
                  <Tooltip title="Add Device Report">
                    <Button type="link" icon={<PlusOutlined />} size="small" />
                  </Tooltip>
                </Link>
                <Tooltip title="Delete Report">
                  <Popconfirm
                    title="Are you sure you want to delete this report?"
                    onConfirm={() => handleDelete(report.id)}
                    okText="Yes"
                    cancelText="No"
                    placement="bottomRight"
                  >
                    <Button type="link" icon={<DeleteFilled />} size="small" />
                  </Popconfirm>
                </Tooltip>
              </>
            )}

            {shouldSign(report) && (
              <Link to={toOfficeCheck(report.id)}>
                <Tooltip title="Sign Report">
                  <Button type="link" icon={<EditOutlined />} size="small" />
                </Tooltip>
              </Link>
            )}
            {report.hasDeviceReport && (
              <>
                <Link to={toOfficeCheck(report.id)}>
                  <Tooltip title="View Report">
                    <Button
                      type="link"
                      icon={<FileSearchOutlined />}
                      size="small"
                    />
                  </Tooltip>
                </Link>
                <Link to={toEditOfficeCheck(report.id)}>
                  <Tooltip title="Edit Report">
                    <Button type="link" icon={<EditFilled />} size="small" />
                  </Tooltip>
                </Link>
              </>
            )}
          </div>
        )
      },
    },
  ]

  const mobileColumns = useMemo(
    () => [
      {
        title: 'Office check',
        key: 'officeCheck',
        render: renderMobileCard({
          canSign,
          editDates,
          warningColor: brandWarning,
          onDelete: handleDelete,
          onEditDateChange: handleEditDateChange,
          onSaveDate: handleSaveDate,
          toEditOfficeCheck,
          toOfficeCheck,
        }),
      },
    ],
    [
      brandWarning,
      canSign,
      editDates,
      handleDelete,
      handleEditDateChange,
      handleSaveDate,
      toEditOfficeCheck,
      toOfficeCheck,
    ],
  )

  const mobileExpandable = useMemo(
    () => ({
      columnWidth: EXPAND_COLUMN_WIDTH,
      expandedRowRender: renderMobileDetails,
    }),
    [],
  )

  const { pageSize, total } = useOfficeChecksPagination(fromDate, toDate)

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/office-checks.xlsx?from_date=${fromDate}&to_date=${toDate}`,
  )

  useFeatureGuard('in_office_checks')

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Office Checks"
        subTitle={practice?.name}
        hideSubTitleOnMobile
        extra={[
          (isPractitioner || isAdmin) && (
            <BatchDownloadButton
              key="download"
              practiceId={practiceId}
              fromDate={fromDate}
              toDate={toDate}
            />
          ),
        ]}
      />
      <RangePicker
        value={[dayjs(fromDate), dayjs(toDate)]}
        className={classes.datePicker}
        ranges={{
          'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        }}
        onChange={handleDateChange}
      />
      {/* Wraps so the result count drops below the filters on narrow screens. */}
      <Flex gap={10} wrap>
        <Filters
          showOfficeCheckFilters
          showSearch
          showDeviceType
          showMedicalProvider
          showOfficeLocation
          defaultValues={DEFAULT_FILTERS}
          currentValues={conditions}
          placeholders={{ officeCheckFilters: 'All' }}
          onSubmit={handleStatusFilter}
          onReset={handleReset}
          className={classes.filters}
        />
        <div className={classes.total}>
          <div className={classes.totalResults}>
            {`${total || 0} result${total !== 1 ? 's' : ''}`}
          </div>
        </div>
      </Flex>
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        expandable={isMobile ? mobileExpandable : undefined}
        dataSource={officeChecks || []}
        loading={pending}
        // The table styles target the desktop Actions column (last cell); on
        // mobile that selector would hit the card itself, so drop them.
        className={isMobile ? undefined : classes.officeChecksTable}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
      {Boolean(officeChecks.length > 0) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}
